import { useEffect, useState } from "react";
import { Clock, Infinity as InfinityIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/components/language-provider";

const MAX_DAYS = 365;
const QUICK = [3, 7, 15, 30, 60, 90];

const TEXTS = {
  pt: {
    title: "Prazo para assinar",
    hint: "Quanto tempo o link de assinatura fica aberto quando quem envia não escolhe uma data.",
    days: "Vence depois de",
    daysUnit: (n: number) => (n === 1 ? "dia" : "dias"),
    none: "Sem prazo",
    noneHint: "O link fica aberto até o documento ser assinado ou cancelado.",
    range: `De 1 a ${MAX_DAYS} dias.`,
    applies: "Vale para os próximos envios; os que já saíram mantêm o prazo que tinham.",
  },
  en: {
    title: "Time to sign",
    hint: "How long the signing link stays open when the sender picks no date.",
    days: "Expires after",
    daysUnit: (n: number) => (n === 1 ? "day" : "days"),
    none: "No deadline",
    noneHint: "The link stays open until the document is signed or cancelled.",
    range: `From 1 to ${MAX_DAYS} days.`,
    applies: "Applies to the next requests; the ones already sent keep their deadline.",
  },
};

/** 0 = no deadline. Calls onSave only with a valid value that changed. */
export function SigningDeadlineSetting({ days, onSave, disabled }: { days: number; onSave: (days: number) => void; disabled?: boolean }) {
  const { language } = useLanguage();
  const tx = TEXTS[language === "en" ? "en" : "pt"];
  const unlimited = days <= 0;
  const [typed, setTyped] = useState(String(days > 0 ? days : 7));
  useEffect(() => { if (days > 0) setTyped(String(days)); }, [days]);

  const commit = (raw: string) => {
    const n = Math.round(Number(raw));
    if (!Number.isFinite(n) || n < 1) { setTyped(String(days > 0 ? days : 7)); return; }
    const value = Math.min(n, MAX_DAYS);
    setTyped(String(value));
    if (value !== days) onSave(value);
  };

  return (
    <div className="space-y-3" data-testid="signing-deadline-setting">
      <div className="space-y-1">
        <p className="flex items-center gap-2 text-sm font-medium"><Clock className="h-4 w-4 text-muted-foreground" />{tx.title}</p>
        <p className="text-sm text-muted-foreground">{tx.hint}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label={tx.title}>
        <div
          role="radio"
          aria-checked={!unlimited}
          tabIndex={0}
          onClick={() => { if (unlimited && !disabled) commit(typed); }}
          onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && unlimited && !disabled) { e.preventDefault(); commit(typed); } }}
          className={`space-y-3 rounded-lg border-2 p-3 transition-smooth ${!unlimited ? "border-primary bg-primary/10" : "cursor-pointer border-border hover:border-primary/40"}`}
          data-testid="deadline-days"
        >
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
            <span>{tx.days}</span>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_DAYS}
              value={typed}
              disabled={disabled}
              onChange={(e) => setTyped(e.target.value)}
              onBlur={(e) => commit(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit((e.target as HTMLInputElement).value); } }}
              onClick={(e) => e.stopPropagation()}
              className="h-8 w-20 text-center"
              aria-label={tx.title}
              data-testid="input-deadline-days"
            />
            <span>{tx.daysUnit(Number(typed) || 0)}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {QUICK.map((q) => (
              <button
                key={q}
                type="button"
                disabled={disabled}
                onClick={(e) => { e.stopPropagation(); commit(String(q)); }}
                className={`rounded-full border px-2.5 py-0.5 text-xs transition ${!unlimited && days === q ? "border-primary bg-primary text-primary-foreground" : "hover:border-primary/50"}`}
                data-testid={`deadline-quick-${q}`}
              >
                {q} {tx.daysUnit(q)}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{tx.range}</p>
        </div>
        <button
          type="button"
          role="radio"
          aria-checked={unlimited}
          disabled={disabled}
          onClick={() => !unlimited && onSave(0)}
          className={`flex flex-col items-start gap-1.5 rounded-lg border-2 p-3 text-left transition-smooth disabled:opacity-60 ${unlimited ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
          data-testid="deadline-none"
        >
          <span className="flex items-center gap-2 text-sm font-medium"><InfinityIcon className="h-4 w-4" />{tx.none}</span>
          <span className="text-xs text-muted-foreground">{tx.noneHint}</span>
        </button>
      </div>
      <p className="text-xs text-muted-foreground">{tx.applies}</p>
    </div>
  );
}
